import { useEffect, useRef, useState } from 'react';
import { api, prepareImageUpload } from '../api.js';
import { useConfirm } from './ConfirmDialog.jsx';

// Photo timeline under the Sensei Score (Phase 2C). Big viewer for
// the active photo (the tank is the pride shot — give it room), a
// horizontal thumb scrubber for history, and Sensei's photo read
// comparing the two latest shots.
//
// The weekly "Tank photo" task seeds the habit; this is where the
// habit pays off.

export function PhotoTimeline({ tank }) {
  const confirm = useConfirm();
  // A viewer can look through the timeline; adding, deleting and the
  // photo read are writes the server refuses them.
  const canWrite = !tank?.role || tank.role === 'owner' || tank.role === 'editor';
  const [photos, setPhotos] = useState(null); // null = loading
  const [activeId, setActiveId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const [diff, setDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  // Collapsed by default — the full viewer + scrubber + diff is a lot
  // of vertical room for something most visits only glance at. The
  // header keeps the habit signal (count + last-photo date); one tap
  // expands. The choice sticks per device.
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem('aqs.photoTimelineOpen') === '1';
    } catch {
      return false;
    }
  });
  const setOpenSticky = (v) => {
    setOpen(v);
    try {
      localStorage.setItem('aqs.photoTimelineOpen', v ? '1' : '0');
    } catch {
      /* private mode — non-fatal */
    }
  };
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const rows = await api(`/api/tanks/${tank.id}/photos`);
      setPhotos(rows || []);
      setActiveId((prev) => (rows?.some((r) => r.id === prev) ? prev : rows?.[0]?.id || null));
    } catch (e) {
      setErr(e.message || 'Could not load photos.');
      setPhotos([]);
    }
  };

  useEffect(() => {
    setPhotos(null);
    setDiff(null);
    setErr('');
    load();
    // Cached read only — costs nothing, renders instantly when present.
    api(`/api/tanks/${tank.id}/photo-diff`)
      .then((d) => {
        if (d && !d.none) setDiff(d);
      })
      .catch(() => {});
  }, [tank.id]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setErr('');
    try {
      // Canvas-first like every other upload: a raw iPhone photo (48 MP,
      // ProRAW) can pass the 15 MB limit, and was refused with a 413 on
      // 2026-09-27. Raw bytes only when the browser cannot decode it.
      const { body, mime } = await prepareImageUpload(file);
      const r = await fetch(`/api/tanks/${tank.id}/photos`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': mime },
        body,
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || `Upload failed (${r.status}).`);
      }
      await load();
      setOpenSticky(true);
    } catch (e) {
      setErr(e.message || 'Could not upload.');
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (id) => {
    const ok = await confirm({
      title: 'Delete this photo from the timeline?',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api(`/api/tanks/${tank.id}/photos/${id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setErr(e.message || 'Could not delete.');
    }
  };

  const runDiff = async () => {
    setDiffLoading(true);
    setErr('');
    try {
      const d = await api(`/api/tanks/${tank.id}/photo-diff`, { method: 'POST' });
      setDiff(d);
    } catch (e) {
      setErr(e.message || 'Sensei could not read the photos right now.');
    } finally {
      setDiffLoading(false);
    }
  };

  if (photos === null) return null; // first paint — no skeleton flash for a side panel

  const active = photos.find((p) => p.id === activeId) || photos[0] || null;

  return (
    <section className="hv-photos" aria-label="Photo timeline">
      <input
        ref={fileRef}
        type="file"
        accept="image/*,.heic,.heif"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          upload(f);
        }}
      />
      <div className="hv-photos-head">
        <button
          type="button"
          className="hv-photos-toggle"
          onClick={() => setOpenSticky(!open)}
          aria-expanded={open}
        >
          <span className={`hv-photos-chevron${open ? ' open' : ''}`} aria-hidden="true">
            ▸
          </span>
          <span className="hv-photos-eyebrow font-heading">Photo timeline</span>
          <span className="hv-photos-meta font-mono">
            {photos.length === 0
              ? 'no photos yet'
              : `${photos.length} · last ${new Date(photos[0].taken_at).toLocaleDateString(
                  undefined,
                  { month: 'short', day: 'numeric' }
                )}`}
          </span>
        </button>
        {canWrite && (
          <button
            type="button"
            className="hv-photos-add font-heading"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? 'Uploading…' : '+ Add photo'}
          </button>
        )}
      </div>

      {!open ? null : photos.length === 0 ? (
        canWrite ? (
          <button
            type="button"
            className="hv-photos-empty"
            onClick={() => fileRef.current?.click()}
          >
            Add your first tank photo. One a week builds the timeline, and with two photos you can
            ask Sensei to compare them for changes in algae, water clarity and equipment.
          </button>
        ) : (
          <p className="hv-photos-empty">No tank photos yet.</p>
        )
      ) : (
        <>
          <div className="hv-photos-viewer">
            <img
              src={`/api/tanks/${tank.id}/photos/${active.id}`}
              alt={`Tank photo from ${new Date(active.taken_at).toLocaleDateString()}`}
            />
            <span className="hv-photos-date font-mono">
              {new Date(active.taken_at).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            </span>
            {canWrite && (
              <button
                type="button"
                className="hv-photos-x"
                onClick={() => removePhoto(active.id)}
                aria-label="Delete this photo"
              >
                ×
              </button>
            )}
          </div>

          {photos.length > 1 && (
            <div className="hv-photos-strip" role="tablist" aria-label="Photo history">
              {photos.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  role="tab"
                  aria-selected={p.id === active.id}
                  className={`hv-photos-thumb${p.id === active.id ? ' active' : ''}`}
                  onClick={() => setActiveId(p.id)}
                >
                  <img src={`/api/tanks/${tank.id}/photos/${p.id}`} alt="" loading="lazy" />
                </button>
              ))}
            </div>
          )}

          {photos.length >= 2 && (
            <div className="hv-photos-diff">
              <div className="hv-photos-diff-eyebrow font-heading">
                Sensei&rsquo;s photo read
                {diff?.created_at && (
                  <span className="font-mono hv-photos-diff-at">
                    {new Date(diff.created_at).toLocaleDateString()}
                  </span>
                )}
              </div>
              {diffLoading ? (
                <p className="hv-photos-diff-text dim">Sensei is comparing the two latest shots…</p>
              ) : diff?.narrative ? (
                <p className="hv-photos-diff-text">{diff.narrative}</p>
              ) : canWrite ? (
                <button type="button" className="hv-photos-diff-cta font-heading" onClick={runDiff}>
                  Compare the two latest photos
                </button>
              ) : (
                <p className="hv-photos-diff-text dim">Not compared yet.</p>
              )}
            </div>
          )}
        </>
      )}
      {err && (
        <p className="hv-photos-err" role="alert">
          {err}
        </p>
      )}
    </section>
  );
}
